import { GalleryCarousel } from 'components/atomic/organisms/gallery-carousel';
import Container from 'components/atomic/atoms/container';
import Blur from 'components/atomic/atoms/blur';
import { Box } from '@mui/material';
import { containerSizes, semanticColors, effects } from 'design-system';
import { DefaultPlaceholder } from './bento-states';
import type { GalleryData } from './types';
import type { ReactNode } from 'react';

interface CarouselVariantProps {
  data: GalleryData;
  onImageClick?: (index: number) => void;
  onSlideChange?: (index: number) => void;
  blur?: boolean;
  badgesSlot?: ReactNode;
  counterSlot?: (current: number, total: number) => ReactNode;
  placeholderSlot?: ReactNode;
  shareSlot?: ReactNode;
}

export function CarouselVariant({
  data,
  onImageClick,
  onSlideChange,
  blur,
  badgesSlot,
  counterSlot,
  placeholderSlot,
  shareSlot,
}: CarouselVariantProps) {
  const hasImages = data.images && data.images.length > 0;

  // Show placeholder when no images. Still render overlay badges (e.g. "Coming
  // Soon") — an image-less listing is exactly where a status badge matters most.
  if (!hasImages) {
    return (
      <Container sx={{ position: 'relative' }} data-testid="gallery-carousel">
        <Box
          sx={{
            width: '100%',
            height: containerSizes.gallery.mobile,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: semanticColors.lightGray,
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            overflow: 'hidden',
            filter: blur ? effects.blur[100] : 'none',
          }}
        >
          {placeholderSlot || <DefaultPlaceholder round />}
        </Box>
        {badgesSlot}
      </Container>
    );
  }

  return (
    <Container sx={{ position: 'relative' }} data-testid="gallery-carousel">
      <Blur active={blur} data-testid="gallery-blur">
        <GalleryCarousel
          images={data.images}
          altPrefix={data.altPrefix || 'Image'}
          imageDescriptions={data.descriptions}
          overlay={badgesSlot}
          renderCounter={counterSlot}
          height={containerSizes.gallery.mobile}
          onSlideChange={onSlideChange}
          onGalleryClick={() => onImageClick?.(0)}
        />
      </Blur>
      {shareSlot}
    </Container>
  );
}
